/**
 * Iter XX.60 — LiveVoiceOverlay.
 *
 * Full-screen dedicated realtime voice UI. No text composer visible
 * while active. Renders states: Connecting / Listening / Thinking /
 * Speaking / Reconnecting / Ending / Fallback.
 *
 * Timer, mute-mic, mute-Gully, End Conversation, optional transcript
 * (collapsed by default per spec).
 *
 * On end (any reason) the overlay closes AND the parent gets an
 * `onClosed({ reason, appended })` callback so it can refresh the
 * chat thread (which now contains the persisted voice turns).
 */
import React, { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Volume2, VolumeX, X, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { createRealtimeVoiceSession, isRealtimeCapable } from "../lib/realtimeVoice";
import { trackVoice } from "../lib/voice";

export default function LiveVoiceOverlay({ onClosed, onFallback }) {
  const [state, setState] = useState("connecting");   // connecting|listening|thinking|speaking|reconnecting|ending|error
  const [elapsed, setElapsed] = useState(0);
  const [micMuted, setMicMuted] = useState(false);
  const [outputMuted, setOutputMuted] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [warnedAt8, setWarnedAt8] = useState(false);
  const sessionRef = useRef(null);
  const startedAtRef = useRef(performance.now());
  const tickRef = useRef(null);

  useEffect(() => {
    if (!isRealtimeCapable()) {
      setState("error");
      setErrorMsg("Live voice isn't supported in this browser.");
      trackVoice("realtime_connection_failed", { reason: "unsupported" });
      return;
    }

    trackVoice("realtime_session_started", {});
    const sess = createRealtimeVoiceSession({
      onEvent: (ev) => {
        switch (ev.type) {
          case "session_created":
            break;
          case "started":
          case "data_channel_open":
            setState("listening");
            trackVoice("realtime_session_connected", {
              time_to_connect_ms: Math.round(performance.now() - startedAtRef.current),
            });
            break;
          case "player_speech_started":
            setState("listening");
            break;
          case "player_speech_stopped":
            setState("thinking");
            break;
          case "thinking":
            setState("thinking");
            break;
          case "speaking":
            setState("speaking");
            break;
          case "assistant_turn_done":
            setState("listening");
            break;
          case "interrupted":
            trackVoice("realtime_interruption", {});
            setState("listening");
            break;
          case "player_transcript":
            if (ev.text) setTranscript(t => [...t, { role: "user", text: ev.text }]);
            trackVoice("realtime_player_turn_completed",
                        { char_count: (ev.text || "").length });
            break;
          case "assistant_transcript":
            if (ev.text) setTranscript(t => [...t, { role: "assistant", text: ev.text }]);
            trackVoice("realtime_assistant_turn_completed",
                        { char_count: (ev.text || "").length });
            break;
          case "warn_cap":
            setWarnedAt8(true);
            break;
          case "hard_cap_hit":
            setErrorMsg("Session ended after 10 minutes.");
            break;
          case "reconnecting":
            setState("reconnecting");
            trackVoice("realtime_reconnect", { attempt: ev.attempt });
            break;
          case "reconnect_failed":
            trackVoice("realtime_connection_failed", { reason: "reconnect_failed" });
            setState("error");
            setErrorMsg("Connection lost. Falling back to voice message.");
            break;
          case "openai_error":
            trackVoice("realtime_connection_failed",
                        { reason: "openai_error",
                          detail: String(ev.detail?.type || "").slice(0, 60) });
            break;
          case "start_failed":
            setState("error");
            setErrorMsg(_startErrorMessage(ev.error));
            trackVoice("realtime_connection_failed",
                        { reason: String(ev.error || "unknown").slice(0, 60) });
            break;
          case "ended": {
            const dur = Math.round(performance.now() - startedAtRef.current);
            trackVoice("realtime_session_ended",
                        { duration_ms: dur, reason: ev.reason });
            if (onClosed) onClosed({ reason: ev.reason, duration_ms: dur });
            break;
          }
          default:
            break;
        }
      },
    });
    sessionRef.current = sess;
    startedAtRef.current = performance.now();
    sess.start().catch(() => {
      // errors already reported via onEvent(start_failed)
    });

    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((performance.now() - startedAtRef.current) / 1000));
    }, 500);

    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
      if (sessionRef.current) sessionRef.current.end("component_unmount");
    };
  }, []);

  const toggleMic = () => {
    const next = !micMuted;
    setMicMuted(next);
    sessionRef.current && sessionRef.current.muteMic(next);
  };
  const toggleOutput = () => {
    const next = !outputMuted;
    setOutputMuted(next);
    sessionRef.current && sessionRef.current.muteOutput(next);
  };
  const endNow = () => {
    setState("ending");
    sessionRef.current && sessionRef.current.end("user_ended");
  };
  const useFallback = () => {
    onFallback && onFallback();
    if (onClosed) onClosed({ reason: "fallback", duration_ms: 0 });
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-[#050505] text-neutral-100 flex flex-col"
      data-testid="live-voice-overlay"
    >
      <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
        <div className="mb-8">
          <StatePill state={state} />
        </div>

        <VoiceIndicator state={state} />

        <div className="mt-10 text-sm text-neutral-500 tabular-nums"
             data-testid="live-voice-timer">
          {formatSec(elapsed)}
        </div>
        {warnedAt8 && state !== "error" && (
          <div className="mt-2 text-xs text-amber-300">
            Two minutes left in this session.
          </div>
        )}

        {errorMsg && (
          <div className="mt-6 max-w-sm text-sm text-neutral-300"
               data-testid="live-voice-error">
            {errorMsg}
            {state === "error" && (
              <div className="mt-4 flex justify-center gap-2">
                <button
                  onClick={useFallback}
                  data-testid="live-voice-fallback"
                  className="px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium"
                >
                  Use voice message instead
                </button>
                <button
                  onClick={() => { if (onClosed) onClosed({ reason: "error_close", duration_ms: 0 }); }}
                  className="px-4 py-2 rounded-full border border-neutral-800 bg-neutral-950 hover:bg-neutral-900 text-neutral-300 text-sm"
                >
                  Back to chat
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Controls */}
      {state !== "error" && (
        <div className="pb-8 pt-6 px-6 flex flex-col items-center gap-4"
             style={{ paddingBottom: "calc(2rem + env(safe-area-inset-bottom, 0px))" }}>
          <div className="flex gap-4">
            <ControlButton onClick={toggleMic} testid="live-voice-mute-mic"
                            active={!micMuted} icon={micMuted ? MicOff : Mic}
                            label={micMuted ? "Mic off" : "Mic on"} />
            <ControlButton onClick={toggleOutput} testid="live-voice-mute-output"
                            active={!outputMuted}
                            icon={outputMuted ? VolumeX : Volume2}
                            label={outputMuted ? "Muted" : "Voice on"} />
            <button
              type="button"
              onClick={endNow}
              data-testid="live-voice-end"
              className="inline-flex flex-col items-center gap-1"
            >
              <div className="w-14 h-14 rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center transition-colors">
                <X size={20} />
              </div>
              <span className="text-xs text-neutral-400">End</span>
            </button>
          </div>

          <button
            type="button"
            onClick={() => setShowTranscript(v => !v)}
            data-testid="live-voice-transcript-toggle"
            className="text-xs text-neutral-500 hover:text-neutral-300 inline-flex items-center gap-1"
          >
            {showTranscript ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
            Transcript
          </button>
          {showTranscript && (
            <div className="w-full max-w-md max-h-40 overflow-y-auto text-xs space-y-2 border border-neutral-900 rounded-2xl bg-neutral-950 p-3"
                 data-testid="live-voice-transcript">
              {transcript.length === 0 && (
                <div className="text-neutral-500">Transcript will appear here.</div>
              )}
              {transcript.map((t, i) => (
                <div key={i}>
                  <span className={t.role === "user"
                    ? "text-neutral-300"
                    : "text-emerald-300"}>
                    {t.role === "user" ? "You" : "Gully"}
                  </span>
                  <span className="text-neutral-400">: {t.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatePill({ state }) {
  const map = {
    connecting: { text: "Connecting…", color: "bg-neutral-800 text-neutral-300" },
    listening: { text: "Listening", color: "bg-emerald-900/50 text-emerald-200" },
    thinking: { text: "Thinking", color: "bg-amber-900/40 text-amber-200" },
    speaking: { text: "Gully is speaking", color: "bg-sky-900/40 text-sky-200" },
    reconnecting: { text: "Reconnecting…", color: "bg-neutral-800 text-neutral-300" },
    ending: { text: "Ending…", color: "bg-neutral-800 text-neutral-300" },
    error: { text: "Connection issue", color: "bg-red-900/40 text-red-200" },
  };
  const cfg = map[state] || map.connecting;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full ${cfg.color} px-4 py-1.5 text-sm font-medium`}
          data-testid="live-voice-state">
      {state === "connecting" || state === "reconnecting" || state === "ending"
        ? <Loader2 size={14} className="animate-spin" />
        : <span className="w-2 h-2 rounded-full bg-current animate-pulse" />}
      {cfg.text}
    </span>
  );
}

function VoiceIndicator({ state }) {
  const active = state === "listening" || state === "speaking" || state === "thinking";
  return (
    <div className="flex items-end gap-1.5 h-16">
      {[0, 1, 2, 3, 4].map(i => (
        <span
          key={i}
          className={`w-1.5 rounded-full ${active ? "bg-emerald-400" : "bg-neutral-700"}`}
          style={{
            height: active ? `${20 + ((i + Math.floor(Date.now() / 200)) % 3) * 12}px` : "10px",
            transition: "height 200ms ease",
          }}
        />
      ))}
    </div>
  );
}

function ControlButton({ onClick, testid, active, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex flex-col items-center gap-1"
    >
      <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-colors ${
        active ? "bg-neutral-800 hover:bg-neutral-700" : "bg-red-900/50 hover:bg-red-900"
      }`}>
        <Icon size={20} />
      </div>
      <span className="text-xs text-neutral-400">{label}</span>
    </button>
  );
}

function formatSec(s) {
  const m = Math.floor(s / 60); const r = s % 60;
  return `${m}:${r < 10 ? "0" : ""}${r}`;
}

function _startErrorMessage(err) {
  const s = String(err || "").toLowerCase();
  if (s.includes("permission")) return "Mic permission denied. You can still type or use voice messages.";
  if (s.includes("openai_api_key_not_configured")) return "Live voice isn't configured yet on this server.";
  if (s.includes("realtime_voice_not_enabled")) return "Live voice isn't enabled on your account yet.";
  if (s.includes("daily_session_limit_reached")) return "You've used today's live voice sessions. Try again tomorrow.";
  if (s.includes("active_session_exists")) return "Another live session is already active for your account.";
  return "Couldn't start the live session. You can still use voice messages.";
}
